"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getIframeURL = exports.isIframe = exports.isAutoplaySupported = exports.isMSESupported = exports.isVolumeReadOnly = exports.isNativeHlsSupported = exports.getSupportedDrm = exports.getSupportedFormats = exports.isSupported = void 0;
const can_autoplay_1 = __importDefault(require("can-autoplay"));
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const common_1 = require("../device/common");
const logger_1 = require("./logger");
const VIDEO_TYPES = [
    'video/mp4; codecs="avc1.42E01E"',
    'audio/mp4; codecs="mp4a.40.2"',
    'video/mp2t; codecs="avc1.42E01E"',
    'video/mp2t; codecs="mp4a.40.2"',
];
function isSupported(checkDrmSupport = false) {
    return new Promise((resolve, reject) => {
        let isDeviceSupported;
        if (window.MediaSource) {
            VIDEO_TYPES.forEach((type) => {
                const isTypeSupported = window.MediaSource.isTypeSupported(type);
                (0, logger_1.info)(`MimeType: ${type}, supported: ${isTypeSupported}`);
                if (isDeviceSupported === undefined) {
                    isDeviceSupported = isTypeSupported;
                }
            });
        }
        else if (isNativeHlsSupported()) {
            isDeviceSupported = true;
        }
        if (isDeviceSupported && checkDrmSupport) {
            getSupportedDrm().then((drm) => {
                if (drm) {
                    resolve();
                }
                reject();
            });
        }
        else if (isDeviceSupported) {
            resolve();
        }
        else {
            reject();
        }
    });
}
exports.isSupported = isSupported;
function getSupportedFormats() {
    var _a;
    if (process.env.NODE_ENV === "development") {
        if ((_a = window.__RED_BEE_MEDIA__) === null || _a === void 0 ? void 0 : _a.supportedFormats) {
            return window.__RED_BEE_MEDIA__.supportedFormats;
        }
    }
    if ((0, common_1.isAppleSafari)()) {
        return ["HLS", "MP3", "MP4"];
    }
    return ["DASH", "HLS", "SMOOTHSTREAMING", "MP3", "MP4"];
}
exports.getSupportedFormats = getSupportedFormats;
function getTestRobustness(keySystem) {
    if (keySystem === js_player_shared_1.KeySystem.WIDEVINE) {
        return "SW_SECURE_CRYPTO";
    }
    return "";
}
// The order matters! The first key system that is supported will be used.
// We _want_ to use PlayReady on Edge because it supports HEVC. However using PlayReady
// on edge breaks SSAI.
const DEFAULT_KEYSYSTEMS = [js_player_shared_1.KeySystem.WIDEVINE, js_player_shared_1.KeySystem.PLAYREADY];
function getSupportedDrm(keySystem, codecs = "avc1.42E01E") {
    if ((0, common_1.isAppleSafari)() &&
        window.WebKitMediaKeys &&
        (!keySystem || keySystem === js_player_shared_1.DRMType.FAIRPLAY)) {
        return Promise.resolve(js_player_shared_1.DRMType.FAIRPLAY);
    }
    if (navigator.requestMediaKeySystemAccess) {
        const promises = [];
        let keySystems = DEFAULT_KEYSYSTEMS;
        if (keySystem) {
            keySystems = [keySystem];
        }
        keySystems.forEach((keySystem) => {
            const tmp = navigator
                .requestMediaKeySystemAccess(keySystem, [
                {
                    videoCapabilities: [
                        {
                            robustness: getTestRobustness(keySystem),
                            contentType: `video/mp4; codecs="${codecs}"`,
                        },
                    ],
                },
            ])
                .then((access) => access.createMediaKeys())
                .then(() => {
                switch (keySystem) {
                    case js_player_shared_1.KeySystem.WIDEVINE:
                        return js_player_shared_1.DRMType.WIDEVINE;
                    case js_player_shared_1.KeySystem.PLAYREADY:
                        return js_player_shared_1.DRMType.PLAYREADY;
                    default:
                        return;
                }
            }, () => undefined);
            promises.push(tmp);
        });
        return Promise.all(promises).then((arr) => {
            const supportedDrm = arr.find((drm) => !!drm);
            return supportedDrm;
        });
    }
    return Promise.resolve(undefined);
}
exports.getSupportedDrm = getSupportedDrm;
function isNativeHlsSupported() {
    if ((0, common_1.isAppleSafari)()) {
        try {
            return !!document
                .createElement("video")
                .canPlayType("application/vnd.apple.mpegurl");
        }
        catch (e) {
            return false;
        }
    }
    return false;
}
exports.isNativeHlsSupported = isNativeHlsSupported;
function delay(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
function isVolumeReadOnly() {
    return __awaiter(this, void 0, void 0, function* () {
        const video = document.createElement("video");
        video.volume = 1; // make sure volume is set to 1
        video.volume = 0.5;
        // on ipad the volume is read-only however instead of doing nothing when setting the volume
        // it will be correctly set however it will be reverted the next tick so we need to wait for that
        // to happen before we can check if the volume is read-only.
        yield delay(0);
        return video.volume === 1;
    });
}
exports.isVolumeReadOnly = isVolumeReadOnly;
function isMSESupported() {
    return !!window.MediaSource;
}
exports.isMSESupported = isMSESupported;
/**
 * Check if autoplay is supported
 * @param  {Boolean}  muted optional parameter, useful if the attempted playback is muted
 * @return {Promise}
 */
function isAutoplaySupported(muted) {
    return __awaiter(this, void 0, void 0, function* () {
        if ((0, common_1.isSamsungTV)() || (0, common_1.isLGTV)()) {
            // canAutoplay doesn't work properly on these devices & we know autoplay is supported so no need to validate it.
            return true;
        }
        try {
            return (yield can_autoplay_1.default.video({ muted: !!muted, inline: true })).result;
        }
        catch (_) {
            return false;
        }
    });
}
exports.isAutoplaySupported = isAutoplaySupported;
function isIframe() {
    try {
        return window.self !== window.top;
    }
    catch (e) {
        // if accessing window.top throws an error we're definitely in an iframe
        return true;
    }
}
exports.isIframe = isIframe;
function getIframeURL() {
    var _a;
    if (isIframe()) {
        try {
            const url = document.referrer || ((_a = window.top) === null || _a === void 0 ? void 0 : _a.location.href);
            if (url) {
                return new URL(url);
            }
        }
        catch (e) {
            // no-op
        }
    }
}
exports.getIframeURL = getIframeURL;
//# sourceMappingURL=helpers.js.map